import logger from '../utils/logger';
/**
 * Generalized Email Preview Hook
 * Reusable hook for email preview functionality across different services
 */

import React, { useState, useCallback, useEffect } from 'react';
import StableMuiDialog from '../common/StableMuiDialog.js';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import { generateEmailPreview, sendEmail, EMAIL_TEMPLATE_TYPES } from '../components/CaseReview/EmailFactory/emailService';
import { useUser } from '../components/UserContext';

/**
 * EmailEditor component using iframe for WYSIWYG editing
 */
const EmailEditor = ({ emailBody, onContentChange }) => {
  const [iframeLoaded, setIframeLoaded] = useState(false);

  useEffect(() => {
    setIframeLoaded(false);
  }, [emailBody]);

  const handleIframeLoad = () => {
    setIframeLoaded(true);
    const iframe = document.querySelector('iframe[title="Email Editor"]');
    if (iframe && iframe.contentDocument) {
      const doc = iframe.contentDocument;
      doc.body.innerHTML = emailBody;
      
      // Add editing functionality
      const script = doc.createElement('script');
      script.textContent = `
        function makeEditable() {
          document.body.contentEditable = true;
          document.body.addEventListener('input', function() {
            if (window.parent && window.parent.postMessage) {
              window.parent.postMessage({
                type: 'contentChange',
                content: document.body.innerHTML
              }, '*');
            }
          });
        }
        makeEditable();
      `;
      doc.head.appendChild(script);
    }
  };

  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data.type === 'contentChange') {
        onContentChange(event.data.content);
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [onContentChange]);

  return (
    <Box sx={{ 
      height: '100%', 
      minHeight: '400px',
      border: '1px solid #ddd', 
      borderRadius: 1,
      display: 'flex',
      flexDirection: 'column'
    }}>
      <iframe
        srcDoc={`
          <!DOCTYPE html>
          <html style="height: 100%;">
          <head>
            <meta charset="UTF-8">
            <meta name="viewport" content="width=device-width, initial-scale=1.0">
            <style>
              html, body { 
                height: 100%;
                margin: 0;
                padding: 0;
              }
              body { 
                font-family: Arial, sans-serif; 
                line-height: 1.6; 
                color: #333; 
                margin: 0; 
                padding: 20px;
                background-color: #ffffff;
                min-height: 100%;
                box-sizing: border-box;
              }
              .email-container {
                max-width: 600px; 
                margin: 0 auto; 
                background-color: #ffffff; 
                padding: 20px;
                min-height: 100%;
                box-sizing: border-box;
              }
              .header {
                margin-bottom: 20px;
                border-bottom: 2px solid #1a73e8;
                padding-bottom: 10px;
              }
              .content {
                margin-bottom: 20px;
              }
              .footer {
                margin-top: 20px;
                padding-top: 10px;
                border-top: 1px solid #e0e0e0;
                font-size: 12px;
                color: #666;
              }
              .data-item { 
                background-color: #f8f9fa; 
                padding: 12px; 
                margin: 2px 0; 
                border-left: 3px solid #1a73e8; 
                border-radius: 4px; 
                font-size: 14px; 
              }
              .stats { 
                display: flex; 
                flex-wrap: wrap; 
                gap: 8px; 
                margin: 8px 0; 
              }
              .stat-item { 
                background-color: #e8f0fe; 
                padding: 12px; 
                border-radius: 4px; 
                min-width: 100px; 
                font-size: 13px; 
              }
              p { 
                font-size: 14px; 
                line-height: 1.5; 
                margin: 2px 0; 
              }
              li { 
                font-size: 14px; 
                margin: 1px 0; 
              }
              [contenteditable="true"]:focus {
                outline: none;
                background-color: rgba(26, 115, 232, 0.05);
              }
            </style>
          </head>
          <body>
            ${emailBody}
          </body>
          </html>
        `}
        title="Email Editor"
        style={{ 
          width: '100%', 
          height: '100%', 
          border: 'none',
          flex: 1,
          minHeight: '400px'
        }}
        onLoad={handleIframeLoad}
      />
    </Box>
  );
};

/**
 * Main Generalized Email Preview Dialog Component
 */
const GeneralizedEmailPreviewDialog = ({ 
  open, 
  onClose, 
  onConfirm, 
  emailData,
  toEmails, 
  setToEmails, 
  ccEmails, 
  setCcEmails, 
  bccEmails, 
  setBccEmails, 
  subject, 
  setSubject, 
  emailBody, 
  setEmailBody, 
  isGenerating = false, 
  templateType = 'offers' 
}) => {
  const [newToEmail, setNewToEmail] = useState('');
  const [newCcEmail, setNewCcEmail] = useState('');
  const [newBccEmail, setNewBccEmail] = useState('');
  const [showCc, setShowCc] = useState(true);
  const [showBcc, setShowBcc] = useState(false);
  const [toEmailError, setToEmailError] = useState(false);
  const [ccEmailError, setCcEmailError] = useState(false);
  const [bccEmailError, setBccEmailError] = useState(false);
  const [editedEmailHtml, setEditedEmailHtml] = useState('');

  // Email validation function
  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email.trim());
  };

  const handleAddToEmail = () => {
    const trimmedEmail = newToEmail.trim();
    if (trimmedEmail && !toEmails.includes(trimmedEmail)) {
      if (isValidEmail(trimmedEmail)) {
        setToEmails([...toEmails, trimmedEmail]);
        setNewToEmail('');
        setToEmailError(false);
      } else {
        setToEmailError(true);
      }
    }
  };

  const handleToEmailChange = (e) => {
    setNewToEmail(e.target.value);
    setToEmailError(false);
  };

  const handleToEmailBlur = () => {
    if (newToEmail.trim()) {
      handleAddToEmail();
    }
  };

  const handleCcEmailBlur = () => {
    if (newCcEmail.trim()) {
      handleAddCcEmail();
    }
  };

  const handleBccEmailBlur = () => {
    if (newBccEmail.trim()) {
      handleAddBccEmail();
    }
  };

  const handleAddCcEmail = () => {
    const trimmedEmail = newCcEmail.trim();
    if (trimmedEmail && !ccEmails.includes(trimmedEmail)) {
      if (isValidEmail(trimmedEmail)) {
        setCcEmails([...ccEmails, trimmedEmail]);
        setNewCcEmail('');
        setCcEmailError(false);
      } else {
        setCcEmailError(true);
      }
    }
  };

  const handleAddBccEmail = () => {
    const trimmedEmail = newBccEmail.trim();
    if (trimmedEmail && !bccEmails.includes(trimmedEmail)) {
      if (isValidEmail(trimmedEmail)) {
        setBccEmails([...bccEmails, trimmedEmail]);
        setNewBccEmail('');
        setBccEmailError(false);
      } else {
        setBccEmailError(true);
      }
    }
  };

  const handleRemoveEmail = (email, type) => {
    if (type === 'to') {
      setToEmails(toEmails.filter(e => e !== email));
    } else if (type === 'cc') {
      setCcEmails(ccEmails.filter(e => e !== email));
    } else if (type === 'bcc') {
      setBccEmails(bccEmails.filter(e => e !== email));
    }
  };

  const handleKeyPress = (e, type) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (type === 'to') handleAddToEmail();
      else if (type === 'cc') handleAddCcEmail();
      else if (type === 'bcc') handleAddBccEmail();
    }
  };

  if (!open) return null;

  return (
    <StableMuiDialog
      open={open}
      onClose={() => onClose(false)}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { 
          height: '85vh',
          borderRadius: '8px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
        }
      }}
    >
      <DialogTitle sx={{ 
        pb: 1, 
        fontSize: '18px', 
        fontWeight: 500,
        color: '#202124',
        borderBottom: '1px solid #dadce0'
      }}>
        Email Preview & Send
      </DialogTitle>
      
      <DialogContent sx={{ 
        p: 0, 
        display: 'flex', 
        flexDirection: 'column', 
        height: '100%',
        minHeight: 0 // Allow content to shrink
      }}>
        <Box sx={{ 
          p: 2, 
          pt: 0, 
          pb: 0,
          flex: 1, 
          display: 'flex', 
          flexDirection: 'column',
          minHeight: 0, // Allow content to shrink
          overflow: 'hidden' // Prevent overflow
        }}>
          {/* Email Recipients - Gmail Style */}
          <Box sx={{
            borderBottom: '1px solid #dadce0',
            backgroundColor: '#ffffff',
            mb: 0.5,
            '&:hover': {
              borderBottomColor: '#1a73e8',
            },
            '&:focus-within': {
              borderBottomColor: '#1a73e8',
            },
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 0 }}>
              <Typography 
                variant="body2" 
                sx={{ 
                  minWidth: '60px', 
                  fontSize: '13px', 
                  color: '#5f6368',
                  fontWeight: 500
                }}
              >
                To:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', flex: 1, minHeight: '32px' }}>
                {toEmails.map((email) => (
                  <Chip
                    key={email}
                    label={email}
                    onDelete={() => handleRemoveEmail(email, 'to')}
                    color="primary"
                    size="small"
                    sx={{
                      height: '24px',
                      fontSize: '13px',
                      margin: '2px',
                      '& .MuiChip-label': {
                        padding: '0 8px',
                      },
                      '& .MuiChip-deleteIcon': {
                        fontSize: '16px',
                      },
                    }}
                  />
                ))}
                <TextField
                  placeholder="Add email address"
                  value={newToEmail}
                  onChange={handleToEmailChange}
                  onBlur={handleToEmailBlur}
                  onKeyPress={(e) => handleKeyPress(e, 'to')}
                  variant="outlined"
                  size="small"
                  autoComplete={isGenerating ? "off" : "section-to email"}
                  error={toEmailError}
                  inputProps={{
                    name: 'toEmail',
                    id: 'to-email',
                    inputMode: 'email',
                    autoCapitalize: 'none',
                    spellCheck: 'false'
                  }}
                  sx={{ 
                    minWidth: '200px',
                    '& .MuiOutlinedInput-root': {
                      height: '32px',
                      fontSize: '14px',
                      backgroundColor: 'transparent',
                      color: toEmailError ? '#d32f2f' : 'inherit',
                      '& fieldset': {
                        border: 'none',
                      },
                      '&:hover fieldset': {
                        border: 'none',
                      },
                      '&.Mui-focused fieldset': {
                        border: 'none',
                      },
                      '&.Mui-error fieldset': {
                        border: 'none',
                      },
                    },
                    '& .MuiInputLabel-root': {
                      fontSize: '13px',
                      color: toEmailError ? '#d32f2f' : '#5f6368',
                      '&.Mui-focused': {
                        color: toEmailError ? '#d32f2f' : '#1a73e8',
                      },
                    },
                    '& .MuiInputBase-input': {
                      color: toEmailError ? '#d32f2f' : 'inherit',
                    },
                  }}
                />
              </Box>
              <Box sx={{ display: 'flex', gap: 1, ml: 2 }}>
                <Button
                  size="small"
                  onClick={() => setShowCc(!showCc)}
                  sx={{ 
                    textTransform: 'none',
                    fontSize: '12px',
                    color: showCc ? '#1a73e8' : '#5f6368',
                    minWidth: 'auto',
                    px: 1
                  }}
                >
                  Cc
                </Button>
                <Button
                  size="small"
                  onClick={() => setShowBcc(!showBcc)}
                  sx={{ 
                    textTransform: 'none',
                    fontSize: '12px',
                    color: showBcc ? '#1a73e8' : '#5f6368',
                    minWidth: 'auto',
                    px: 1
                  }}
                >
                  Bcc
                </Button>
              </Box>
            </Box>

             {/* CC Field - Only show when toggled */}
             {showCc && (
               <Box sx={{ display: 'flex', alignItems: 'center', mb: 0 }}>
                 <Typography 
                   variant="body2" 
                   sx={{ 
                     minWidth: '60px', 
                     fontSize: '13px', 
                     color: '#5f6368',
                     fontWeight: 500
                   }}
                 >
                   Cc:
                 </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', flex: 1, minHeight: '32px' }}>
                  {ccEmails.map((email) => (
                    <Chip
                      key={email}
                      label={email}
                      onDelete={() => handleRemoveEmail(email, 'cc')}
                      color="secondary"
                      size="small"
                      sx={{
                        height: '24px',
                        fontSize: '13px',
                        margin: '2px',
                        '& .MuiChip-label': {
                          padding: '0 8px',
                        },
                        '& .MuiChip-deleteIcon': {
                          fontSize: '16px',
                        },
                      }}
                    />
                  ))}
                  <TextField
                    placeholder="Add CC email address"
                    value={newCcEmail}
                    onChange={(e) => {
                      setNewCcEmail(e.target.value);
                      setCcEmailError(false);
                    }}
                    onBlur={handleCcEmailBlur}
                    onKeyPress={(e) => handleKeyPress(e, 'cc')}
                    variant="outlined"
                    size="small"
                    autoComplete={isGenerating ? "off" : "section-cc email"}
                    error={ccEmailError}
                    inputProps={{
                      name: 'ccEmail',
                      id: 'cc-email',
                      inputMode: 'email',
                      autoCapitalize: 'none',
                      spellCheck: 'false'
                    }}
                    sx={{ 
                      minWidth: '200px',
                      '& .MuiOutlinedInput-root': {
                        height: '32px',
                        fontSize: '14px',
                        backgroundColor: 'transparent',
                        color: ccEmailError ? '#d32f2f' : 'inherit',
                        '& fieldset': {
                          border: 'none',
                        },
                        '&:hover fieldset': {
                          border: 'none',
                        },
                        '&.Mui-focused fieldset': {
                          border: 'none',
                        },
                        '&.Mui-error fieldset': {
                          border: 'none',
                        },
                      },
                      '& .MuiInputLabel-root': {
                        fontSize: '13px',
                        color: ccEmailError ? '#d32f2f' : '#5f6368',
                        '&.Mui-focused': {
                          color: ccEmailError ? '#d32f2f' : '#1a73e8',
                        },
                      },
                      '& .MuiInputBase-input': {
                        color: ccEmailError ? '#d32f2f' : 'inherit',
                      },
                    }}
                  />
                </Box>
              </Box>
            )}

            {/* BCC Field - Only show when toggled */}
            {showBcc && (
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 0 }}>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    minWidth: '60px', 
                    fontSize: '13px', 
                    color: '#5f6368',
                    fontWeight: 500
                  }}
                >
                  Bcc:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', flex: 1, minHeight: '32px' }}>
                  {bccEmails.map((email) => (
                    <Chip
                      key={email}
                      label={email}
                      onDelete={() => handleRemoveEmail(email, 'bcc')}
                      color="default"
                      size="small"
                      sx={{
                        height: '24px',
                        fontSize: '13px',
                        margin: '2px',
                        '& .MuiChip-label': {
                          padding: '0 8px',
                        },
                        '& .MuiChip-deleteIcon': {
                          fontSize: '16px',
                        },
                      }}
                    />
                  ))}
                  <TextField
                    placeholder="Add BCC email address"
                    value={newBccEmail}
                    onChange={(e) => {
                      setNewBccEmail(e.target.value);
                      setBccEmailError(false);
                    }}
                    onBlur={handleBccEmailBlur}
                    onKeyPress={(e) => handleKeyPress(e, 'bcc')}
                    variant="outlined"
                    size="small"
                    autoComplete={isGenerating ? "off" : "section-bcc email"}
                    error={bccEmailError}
                    inputProps={{
                      name: 'bccEmail',
                      id: 'bcc-email',
                      inputMode: 'email',
                      autoCapitalize: 'none',
                      spellCheck: 'false'
                    }}
                    sx={{ 
                      minWidth: '200px',
                      '& .MuiOutlinedInput-root': {
                        height: '32px',
                        fontSize: '14px',
                        backgroundColor: 'transparent',
                        color: bccEmailError ? '#d32f2f' : 'inherit',
                        '& fieldset': {
                          border: 'none',
                        },
                        '&:hover fieldset': {
                          border: 'none',
                        },
                        '&.Mui-focused fieldset': {
                          border: 'none',
                        },
                        '&.Mui-error fieldset': {
                          border: 'none',
                        },
                      },
                      '& .MuiInputLabel-root': {
                        fontSize: '13px',
                        color: bccEmailError ? '#d32f2f' : '#5f6368',
                        '&.Mui-focused': {
                          color: bccEmailError ? '#d32f2f' : '#1a73e8',
                        },
                      },
                      '& .MuiInputBase-input': {
                        color: bccEmailError ? '#d32f2f' : 'inherit',
                      },
                    }}
                  />
                </Box>
              </Box>
            )}
          </Box>

          {/* Subject Line */}
          <Box sx={{ 
            borderBottom: '1px solid #dadce0',
            backgroundColor: '#ffffff',
            '&:hover': {
              borderBottomColor: '#1a73e8',
            },
            '&:focus-within': {
              borderBottomColor: '#1a73e8',
            },
          }}>
            <TextField
              fullWidth
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Subject"
              variant="outlined"
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  height: '32px',
                  fontSize: '14px',
                  backgroundColor: 'transparent',
                  '& fieldset': {
                    border: 'none',
                  },
                  '&:hover fieldset': {
                    border: 'none',
                  },
                  '&.Mui-focused fieldset': {
                    border: 'none',
                  },
                },
                '& .MuiInputBase-input': {
                  color: 'inherit',
                },
              }}
            />
          </Box>

          {/* Email Body Editor */}
          <Box sx={{ 
            flex: 1, 
            display: 'flex', 
            flexDirection: 'column', 
            minHeight: 0,
            overflow: 'hidden'
          }}>
            {isGenerating ? (
              <Box sx={{ 
                flex: 1,
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center',
                minHeight: '400px',
                backgroundColor: '#f8f9fa',
                border: '1px solid #ddd',
                borderRadius: 1
              }}>
                <Box sx={{ textAlign: 'center' }}>
                  <CircularProgress size={40} />
                  <Typography variant="body2" sx={{ mt: 2, color: '#5f6368' }}>
                    Generating email preview...
                  </Typography>
                </Box>
              </Box>
            ) : (
              <EmailEditor 
                emailBody={emailBody} 
                onContentChange={setEditedEmailHtml}
              />
            )}
          </Box>

          {/* Error Display */}
          {emailData?.error && (
            <Alert severity="error">
              Error generating email preview: {emailData.error}
            </Alert>
          )}
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ 
        p: 2, 
        borderTop: '1px solid #dadce0',
        backgroundColor: '#f8f9fa',
        justifyContent: 'flex-end',
        gap: 1
      }}>
        <Button 
          onClick={() => onClose(false)}
          sx={{ 
            textTransform: 'none',
            fontSize: '14px',
            color: '#5f6368'
          }}
        >
          Cancel
        </Button>
        <Button 
          onClick={() => onClose(true)} 
          variant="contained"
          disabled={toEmails.length === 0 || !subject.trim()}
          sx={{ 
            textTransform: 'none',
            fontSize: '14px',
            fontWeight: 500,
            px: 3,
            py: 1
          }}
        >
          Send Email
        </Button>
      </DialogActions>
    </StableMuiDialog>
  );
};

/**
 * Hook for managing email preview dialog state
 * @param {Object} downloadButtonProps - Props for download button component
 * @returns {Object} Hook state and functions
 */
export const useGeneralizedEmailPreview = (downloadButtonProps = {}) => {
  const { user } = useUser();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogCallback, setDialogCallback] = useState(null);
  const [emailData, setEmailData] = useState(null);
  const [toEmails, setToEmails] = useState([]);
  const [ccEmails, setCcEmails] = useState([]);
  const [bccEmails, setBccEmails] = useState([]);
  const [subject, setSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [editedEmailHtml, setEditedEmailHtml] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [selectedRound, setSelectedRound] = useState(1);
  const [maxRounds, setMaxRounds] = useState(1);
  const [templateType, setTemplateType] = useState(EMAIL_TEMPLATE_TYPES.OFFERS);
  
  /**
   * Show email preview dialog
   * @param {Object} options - Email preview options
   * @param {string} options.templateType - Type of email template
   * @param {Array} options.data - Service-specific data
   * @param {string} options.downloadUrl - S3 download URL (single URL)
   * @param {string} options.offers_download_url - Offers download URL (for combined templates)
   * @param {string} options.workups_download_url - Workups download URL (for combined templates)
   * @param {string} options.csvFilename - CSV filename
   * @param {Object} options.caseInfo - Case count information
   * @param {Object} options.user - User information
   * @param {string} options.stipulationsUrl - Stipulations URL (optional)
   * @param {Function} callback - Callback function for dialog result
   */
  const showDialog = useCallback(async (options, callback) => {
    
    const {
      templateType = EMAIL_TEMPLATE_TYPES.OFFERS,
      data,
      downloadUrl,
      offers_download_url,
      workups_download_url,
      csvFilename,
      caseInfo,
      stipulationsUrl
    } = options;

    setTemplateType(templateType);
    setDialogCallback(() => callback);
    setDialogOpen(true);
    setIsGenerating(true);
    
    // Clear previous email content while generating
    setEmailBody('');
    setSubject('');
    setEditedEmailHtml('');

    try {
      const previewResult = await generateEmailPreview({
        templateType,
        data,
        downloadUrl,
        offers_download_url,
        workups_download_url,
        csvFilename,
        caseInfo,
        user,
        stipulationsUrl
      });

      if (previewResult.success) {
        setEmailData({
          templateType,
          data,
          downloadUrl,
          offers_download_url,
          workups_download_url,
          csvFilename,
          caseInfo,
          user
        });
        // Set TO emails from assessor database lookup
        setToEmails(previewResult.toEmails || []);
        
        // Set CC emails - user's email is the default CC
        const defaultCcEmails = [];
        if (user?.email) {
          defaultCcEmails.push(user.email);
        }
        // Add any additional CC emails from backend if they exist
        const backendCcEmails = previewResult.ccEmails || [];
        backendCcEmails.forEach(email => {
          if (email && !defaultCcEmails.includes(email)) {
            defaultCcEmails.push(email);
          }
        });
        setCcEmails(defaultCcEmails);
        
        setBccEmails(previewResult.bccEmails || []);
        setSubject(previewResult.subject || '');
        setEmailBody(previewResult.body || '');
        setEditedEmailHtml('');
        setMaxRounds(1);
        setSelectedRound(1);
      } else {
        logger.error('❌ [GENERALIZED EMAIL PREVIEW] Failed to generate email preview:', previewResult.error);
        // Still show dialog but with error state
        setEmailData({
          templateType,
          data,
          downloadUrl,
          offers_download_url,
          workups_download_url,
          csvFilename,
          caseInfo,
          user,
          error: previewResult.error
        });
        setSubject('Error generating email preview');
        setEmailBody(`<p>Error: ${previewResult.error}</p>`);
      }
    } catch (error) {
      logger.error('❌ [GENERALIZED EMAIL PREVIEW] Error in email preview generation:', error);
      setEmailData({
        templateType,
        data,
        downloadUrl,
        offers_download_url,
        workups_download_url,
        csvFilename,
        caseInfo,
        user,
        error: error.message
      });
      setSubject('Error generating email preview');
      setEmailBody(`<p>Error: ${error.message}</p>`);
    } finally {
      setIsGenerating(false);
    }
  }, [user]);

  /**
   * Handle dialog close
   * @param {boolean} confirmed - Whether user confirmed the action
   */
  const handleDialogClose = useCallback((confirmed) => {
    // Extract the latest content from the iframe before closing
    let finalEmailBody = editedEmailHtml || emailBody;
    
    if (confirmed && emailData) {
      // Try to get the latest content from the iframe
      const iframe = document.querySelector('iframe[title="Email Editor"]');
      if (iframe && iframe.contentDocument) {
        const doc = iframe.contentDocument;
        let rawHtml = "<!DOCTYPE html>\n" + doc.documentElement.outerHTML;
        rawHtml = rawHtml.replace(/<script>[^<]*makeEditable[\s\S]*?<\/script>/gi, "");
        const clean = rawHtml
          .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '')
          .replace(/on\w+="[^"]*"/gi, '')
          .replace(/javascript:/gi, '');
        
        // Extract just the body content
        const bodyMatch = clean.match(/<body[^>]*>([\s\S]*)<\/body>/i);
        const bodyContent = bodyMatch ? bodyMatch[1] : clean;
        const finalContent = bodyContent.replace(/\s*data-editable="true"/g, '');
        
        if (finalContent) {
          finalEmailBody = finalContent;
        }
      }
    }
    
    setDialogOpen(false);
    if (dialogCallback) {
      dialogCallback(confirmed, {
        toEmails,
        ccEmails,
        bccEmails,
        subject,
        emailBody: finalEmailBody,
        emailData,
        selectedRound,
        templateType
      });
      setDialogCallback(null);
    }
  }, [editedEmailHtml, emailBody, emailData, toEmails, ccEmails, bccEmails, subject, selectedRound, templateType, dialogCallback]);

  /**
   * Set generating state
   * @param {boolean} generating - Whether currently generating
   */
  const setGenerating = useCallback((generating) => {
    setIsGenerating(generating);
  }, []);

  /**
   * Send email after preview confirmation
   * @param {Object} emailPreviewData - Data from email preview dialog
   * @returns {Promise<Object>} Send result
   */
  const sendEmailFromPreview = useCallback(async (emailPreviewData) => {
    const { emailData, toEmails, ccEmails, bccEmails, subject, emailBody } = emailPreviewData;
    
    if (!emailData) {
      return { success: false, error: 'No email data available' };
    }

    // Determine fileUrl - can be a single downloadUrl or an object with multiple URLs
    let fileUrl = emailData.downloadUrl;

    logger.log('email preview data', emailPreviewData);
    
    // If we have separate offers and workups URLs, create an object
    if (emailData.offers_download_url || emailData.workups_download_url) {
      fileUrl = {
        offers_download_url: emailData.offers_download_url,
        workups_download_url: emailData.workups_download_url
      };
    }

    logger.log('fileUrl', fileUrl);
    
    return await sendEmail({
      templateType: emailData.templateType,
      data: emailData.data,
      fileUrl,
      toEmails,
      ccEmails,
      bccEmails,
      subject,
      emailBody,
      user: emailData.user,
      caseInfo: emailData.caseInfo
    });
  }, []);

  const returnObject = {
    // Dialog state
    dialogOpen,
    emailData,
    toEmails,
    setToEmails,
    ccEmails,
    setCcEmails,
    bccEmails,
    setBccEmails,
    subject,
    setSubject,
    emailBody,
    setEmailBody,
    editedEmailHtml,
    setEditedEmailHtml,
    isGenerating,
    selectedRound,
    setSelectedRound,
    maxRounds,
    setMaxRounds,
    templateType,
    
    // Dialog functions
    showDialog,
    handleDialogClose,
    setGenerating,
    sendEmailFromPreview
  };
  
  
  // Add the dialog component to the return object
  returnObject.dialogComponent = (
    <GeneralizedEmailPreviewDialog
      open={dialogOpen}
      onClose={handleDialogClose}
      onConfirm={handleDialogClose}
      emailData={emailData}
      toEmails={toEmails}
      setToEmails={setToEmails}
      ccEmails={ccEmails}
      setCcEmails={setCcEmails}
      bccEmails={bccEmails}
      setBccEmails={setBccEmails}
      subject={subject}
      setSubject={setSubject}
      emailBody={emailBody}
      setEmailBody={setEmailBody}
      isGenerating={isGenerating}
      templateType={templateType}
    />
  );
  
  return returnObject;
};
